import { useCallback, useMemo, useState } from "react";
import Dialog from "../../../Common/Dialog/Dialog";
import ItemList from "../../../Common/ItemList/ItemList";
import { DialogContent, Grid } from "@mui/material";
import Address from "./Address";
import AddressSkeleton from "./AddressSkeleton";
import { UserAddressStore } from "../../../UserData/_stores/UserAddressStore";
import DialogTitle from "../../../Common/Dialog/DialogTitle";
import { useTranslation } from "react-i18next";
import EditAddressDialog from "./EditAddressDialog";
import AccountEmptyCard from "../../AccountEmptyCard";

const LIMIT = 12;

interface Props {
  opened: boolean
  onClose: () => void
  onSelect: (id:number) => void
  emptyStateDescription?: string
}

export default function SelectAddressDialog(props: Props) {
  const [t] = useTranslation('account-v2')
  const [addDialogOpened, setAddDialogOpened] = useState(false)

  const loadingGrid = useMemo(() => {
    const listSkeletons = []

    for (let i = 0; i < LIMIT; i++) {
      listSkeletons.push(<AddressSkeleton key={i} />)
    }

    return listSkeletons
  }, [])

  const renderAddress = useCallback((item: UserAddressStore) => {
    return <Address 
      address={item} 
      onSelect={props.onSelect}
    />
  }, [props.onSelect])

  const onAddOpen = useCallback(() => {
    setAddDialogOpened(true)
  }, [])

  const onAddClose = useCallback(() => {
    setAddDialogOpened(false)
  }, [])

  const renderList = useCallback((items: JSX.Element | null, links: JSX.Element | null, loading: boolean) => {
    return <>
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {loading
          ? loadingGrid
          : (items ?? <Grid item xs={12} md={6} lg={4}>
            <AccountEmptyCard
              title={t('No saved addresses yet')}
              description={props.emptyStateDescription ?? t('Add an address to use it for your store billing settings.')}
              actionLabel={t('Add address')}
              onAction={onAddOpen}
            />
          </Grid>)
        }
      </Grid>

      {links}
    </>
  }, [loadingGrid, onAddOpen, props.emptyStateDescription, t])

  return <Dialog
    open={props.opened}
    maxWidth="md"
    fullWidth
    onClose={props.onClose}
    showCloseButton
    ornaments="shapes-2"
  >
    <DialogTitle>{t('Select address')}</DialogTitle>
    <DialogContent>
      <ItemList
        dataType="addresses"
        controlled={true}
        item={renderAddress}
        limit={LIMIT}
        render={renderList}
      />
    </DialogContent>

    <EditAddressDialog
      opened={addDialogOpened}
      onClose={onAddClose}
    />
  </Dialog>
}
